<template>
    <!-- 86bc63aqb: the self report's price in a summary card above the card form.
         86bce6r42: shared by the billing page (older reports) and the unlock popup. -->
    <div class="p-4 bg-white rounded-lg border border-gray-200" data-test="payment-summary">
        <h3 class="text-sm font-semibold text-gray-900 mb-3">Payment Summary</h3>
        <div class="space-y-2 text-sm">
            <!-- 86bcbu2dc: a collection or eviction check lists the rest of the report as free. -->
            <div class="space-y-0.5" data-test="summary-lines">
                <div
                    v-for="line in summaryLines"
                    :key="line.label"
                    class="flex justify-between gap-4 text-gray-700"
                >
                    <span>{{ line.label }}</span>
                    <span v-if="line.amount !== null">${{ line.amount }}</span>
                    <span v-else class="italic">Free</span>
                </div>
            </div>

            <!-- 86bcbu2dc: the discount code is already entered; the renter applies it
                 with one click while the countdown runs. -->
            <div
                v-if="showDiscountOffer"
                class="rounded-lg border border-blue-100 bg-blue-50 p-3"
                data-test="discount-offer"
            >
                <label for="discount-code" class="block font-medium text-gray-900">Discount code</label>
                <div class="mt-1 flex flex-wrap items-center gap-3">
                    <input
                        id="discount-code"
                        v-model="discountCode"
                        type="text"
                        class="w-40 rounded-md border-gray-300 text-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                    >
                    <button
                        type="button"
                        class="inline-flex items-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
                        data-test="apply-discount"
                        :disabled="changingDiscount"
                        @click="applyDiscount"
                    >
                        {{ changingDiscount ? 'Applying...' : 'Apply Discount' }}
                    </button>
                    <span class="text-gray-700">Your {{ discount.percent }}% off code is already entered. Apply it to save ${{ discount.amount }}.</span>
                </div>
                <p class="mt-2 font-semibold text-red-600" data-test="discount-countdown" aria-live="off">
                    Claim this discount before it disappears: {{ countdown }}
                </p>
            </div>

            <div
                v-if="discount.applied"
                class="flex justify-between gap-4 text-green-700"
                data-test="discount-applied"
            >
                <!-- 86bcfa7n0 (Rich): no check mark, and his wording. -->
                <span class="flex items-center gap-2">
                    {{ discount.percent }}% Discount Successfully Applied
                    <button
                        type="button"
                        class="text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
                        data-test="remove-discount"
                        :disabled="changingDiscount"
                        @click="removeDiscount"
                    >
                        Remove
                    </button>
                </span>
                <span>−${{ discount.amount }}</span>
            </div>

            <!-- 86bce6r42: the "leaving so soon" discount, taken on top of the code. -->
            <div
                v-if="leaving.applied"
                class="flex justify-between gap-4 text-green-700"
                data-test="leaving-discount-applied"
            >
                <span class="flex items-center gap-2">
                    <font-awesome-icon :icon="faCheck" aria-hidden="true" />
                    Extra {{ leaving.percent }}% off
                </span>
                <span>−${{ leaving.amount }}</span>
            </div>

            <div class="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-900">
                <span>Total</span>
                <span data-test="cart-total">${{ amount }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'

const props = defineProps({
    reportId: {
        type: [String, Number],
        required: true,
    },
    summaryLines: {
        type: Array,
        default: () => [],
    },
    amount: {
        type: String,
        required: true,
    },
    discount: {
        type: Object,
        required: true,
    },
    leaving: {
        type: Object,
        default: () => ({ applied: false, percent: 0, amount: '0.00' }),
    },
    // The parent owns the countdown (countdown.js) so it can act on it too.
    secondsLeft: {
        type: Number,
        default: 0,
    },
    countdown: {
        type: String,
        default: '0:00',
    },
})

const discountCode = ref(props.discount.code)
const changingDiscount = ref(false)

watch(() => props.discount.code, (code) => {
    discountCode.value = code
})

const showDiscountOffer = computed(() => props.discount.open && !props.discount.applied && props.secondsLeft > 0)

function changeDiscount(routeName, data) {
    changingDiscount.value = true
    // Kept state: inside the unlock popup, the popup stays open while the
    // page reloads with the new total.
    router.post(route(routeName, props.reportId), data, {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
            changingDiscount.value = false
        },
    })
}

function applyDiscount() {
    changeDiscount('reports.discount.apply', { code: discountCode.value })
}

function removeDiscount() {
    changeDiscount('reports.discount.remove', {})
}
</script>
